import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid2 as Grid,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  CustomLabel,
  ErrorDisplay,
  FormInput,
} from "@primary-plus/shared-components";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { useDebounce } from "use-debounce";
import {
  usePatientControllerServiceGetAllPatient,
  useUserControllerServiceGetAllProviderAndStaffUser,
  useUserControllerServiceSendInvite,
} from "../../../../services/queries";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { theme } from "../../../utils/theme";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store/store";

const validationSchema = yup.object().shape({
  selectedUsers: yup.array().of(
    yup.object().shape({
      value: yup.string(),
      label: yup.string(),
      email: yup.string(),
      displayText: yup.string(),
    }),
  ),
  emailInputs: yup
    .array()
    .of(
      yup
        .string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Please enter a valid email address",
        ),
    ),
});

function Invite({
  isInviteModalOpen,
  setIsInviteModalOpen,
  callLink,
  appointmentUuid,
}: {
  isInviteModalOpen: boolean;
  setIsInviteModalOpen: (isInviteModalOpen: boolean) => void;
  callLink: string;
  appointmentUuid: string;
}) {
  const [userOptions, setUserOptions] = useState<
    Array<{ value: string; label: string; email: string; displayText: string }>
  >([]);
  const [searchUsers, setSearchUsers] = useState<string | null>(null);
  const [debounceSearch] = useDebounce(searchUsers, 500);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const { page, pageSize } = useSelector((store: RootState) => store.table);
  const { mutateAsync: sendInvite, isPending: isSendingInvite } =
    useUserControllerServiceSendInvite({
      onSuccess: (response) => {
        const message = response?.message || "Invite sent successfully";
        setSuccessMessage(message);
        setTimeout(() => {
          setSuccessMessage(null);
          setIsInviteModalOpen(false);
        }, 2000);
      },
      onError: (error) => {},
    });
  const [isCustomEmail, setIsCustomEmail] = useState(false);

  const {
    options: userListOptions,
    handleScroll: handleUsersScroll,
    isFetching: isUsersFetching,
  } = useInfiniteScroll(
    (page) =>
      useUserControllerServiceGetAllProviderAndStaffUser({
        page,
        pageSize,
        searchString: debounceSearch || undefined,
        sortBy: "firstName",
        sortDirection: "ASC",
      }),
    [debounceSearch],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      selectedUsers: [] as Array<{
        value: string;
        label: string;
        email: string;
        displayText: string;
      }>,
      emailInputs: [],
    },
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (userListOptions && userListOptions.length > 0) {
      const uniqueOptionsMap = new Map<string, any>();
      userListOptions.forEach((user: any) => {
        if (user && user.uuid) {
          const label = `${user.firstName || ""} ${user.lastName || ""}`.trim();
          const displayText = `${user.firstName || ""} ${user.lastName || ""} (${user.email})`;
          uniqueOptionsMap.set(user.uuid, {
            value: user.uuid,
            label: label || "Unknown User",
            email: user.email,
            displayText: displayText,
          });
        }
      });
      const usersArray = Array.from(uniqueOptionsMap.values());
      setUserOptions(usersArray);
    }
  }, [userListOptions]);

  useEffect(() => {
    if (isInviteModalOpen) {
      reset();
    }
  }, [isInviteModalOpen, reset]);

  const handleSetValue = (event) => {
    const { name, value } = event.target;
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
  };

  const handleFormSubmit = async (data) => {
    const emails = [];

    if (data.selectedUsers && data.selectedUsers.length > 0) {
      data.selectedUsers.forEach((selectedUser) => {
        if (selectedUser.email) {
          emails.push(selectedUser.email);
        } else {
          const userOption = userOptions.find(
            (option) => option.value === selectedUser.value,
          );
          if (userOption?.email) {
            emails.push(userOption.email);
          }
        }
      });
    }

    if (data.emailInputs && data.emailInputs.length > 0) {
      const validEmails = data.emailInputs
        .filter((email) => email && email.trim() !== "")
        .map((email) => email.trim());
      emails.push(...validEmails);
    }

    const uniqueEmails = [...new Set(emails)];

    if (uniqueEmails.length > 0 && appointmentUuid) {
      try {
        await sendInvite({
          appointmentUuid,
          userEmails: uniqueEmails,
        });
      } catch (error) {}
    }
  };

  return (
    <Modal
      open={isInviteModalOpen}
      onClose={() => setIsInviteModalOpen(false)}
      sx={{ zIndex: 12000 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          zIndex: 12001,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Invite Participants
        </Typography>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={12}>
              <Controller
                name="selectedUsers"
                control={control}
                render={({ field }) => (
                  <Box>
                    <CustomLabel label="Select Users" isRequired={false} />
                    <Autocomplete
                      multiple
                      options={userOptions}
                      getOptionLabel={(option) => option.displayText}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      value={field.value || []}
                      onChange={(event, newValue) => {
                        field.onChange(newValue);
                      }}
                      onInputChange={(event, newInputValue) => {
                        setSearchUsers(newInputValue);
                      }}
                      renderTags={() => null}
                      loading={isUsersFetching}
                      loadingText="Loading..."
                      noOptionsText={
                        isUsersFetching ? "Loading..." : "No Users Found"
                      }
                      ListboxProps={{
                        onScroll: (event) => {
                          const listboxNode = event.currentTarget;
                          if (
                            listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 10
                          ) {
                            handleUsersScroll();
                          }
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select users to invite"
                          error={Boolean(errors?.selectedUsers)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              minHeight: "36px",
                              padding: "0 8px",
                            },
                            "& .MuiInputBase-input": {
                              padding: 0,
                            },
                          }}
                        />
                      )}
                      slotProps={{
                        popper: { sx: { zIndex: 12000 } },
                        paper: { sx: { zIndex: 12000 } },
                      }}
                    />

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {(field.value || []).map((option, index) => (
                        <Chip
                          key={option.value}
                          label={option.displayText}
                          onDelete={() => {
                            const currentValue = field.value || [];
                            const newValue = currentValue.filter(
                              (_, i) => i !== index,
                            );
                            field.onChange(newValue);
                          }}
                          sx={{
                            backgroundColor: theme?.palette?.primary?.light,
                            color: theme?.palette?.primary?.main,
                          }}
                        />
                      ))}
                    </Box>
                    <ErrorDisplay errors={errors?.selectedUsers} />
                  </Box>
                )}
              />

              {userOptions.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  Loading users...
                </Typography>
              )}
            </Grid>

            <Grid size={12}>
              <Controller
                name="emailInputs"
                control={control}
                render={({ field }) => (
                  <>
                    <FormInput
                      control="mui-freesolo"
                      {...field}
                      label="Email Addresses"
                      placeholder="Enter email addresses"
                      error={errors}
                      isError={Boolean(errors?.emailInputs)}
                      onChange={(event: any) => {
                        handleSetValue(event);
                        setIsCustomEmail(false);
                      }}
                      onInputChange={(inputValue: string) => {
                        if (inputValue.trim() !== "") {
                          setIsCustomEmail(true);
                        } else {
                          setIsCustomEmail(false);
                        }
                      }}
                    />
                    {errors?.emailInputs && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.error.main,
                          fontSize: "12px",
                          mt: 0.5,
                        }}
                      >
                        {Array.isArray(errors.emailInputs)
                          ? errors.emailInputs.find((e) => e?.message)?.message
                          : errors.emailInputs.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" sx={{ mt: 2 }}>
            Or share this link directly:
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              wordBreak: "break-all",
            }}
          >
            {callLink}
          </Box>

          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setIsInviteModalOpen(false)}
              disabled={isSendingInvite}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await navigator.clipboard.writeText(callLink);
                setCopyMessage("Link copied to clipboard");
                setTimeout(() => {
                  setCopyMessage(null);
                }, 2000);
              }}
              disabled={isSendingInvite}
            >
              Copy Link
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                isCustomEmail ? false : isSendingInvite || !isDirty || !isValid
              }
            >
              Send Invite
            </Button>
          </Box>

          {copyMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {copyMessage}
            </Alert>
          )}
        </form>
      </Box>
    </Modal>
  );
}

export default Invite;
